import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Pencil, X, ExternalLink } from 'lucide-react';
import api from '../../services/api';
import Modal from '../../components/Modal';

type Tier = 'free' | 'basic' | 'pro' | 'pro_plus';
type BillingCycle = 'monthly' | 'yearly' | null;

interface KiwifyMapping {
  id: number;
  kiwify_plan_id: string;
  label: string | null;
}

interface SubscriptionPlan {
  id: number;
  name: string;
  slug: string;
  tier: Tier;
  billing_cycle: BillingCycle;
  price_monthly: number | null;
  price_yearly: number | null;
  max_wheels: number;
  max_products: number;
  max_spins_month: number;
  features: string[] | null;
  is_active: boolean;
  kiwify_checkout_url: string | null;
  created_at: string;
  updated_at: string;
  kiwify_mappings: KiwifyMapping[];
  companies_count: number;
}

interface FormState {
  name: string;
  slug: string;
  tier: Tier;
  billing_cycle: '' | 'monthly' | 'yearly';
  price_monthly: string;
  price_yearly: string;
  max_wheels: string;
  max_products: string;
  max_spins_month: string;
  features: string; // textarea, one per line
  kiwify_checkout_url: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  slug: '',
  tier: 'basic',
  billing_cycle: 'monthly',
  price_monthly: '',
  price_yearly: '',
  max_wheels: '0',
  max_products: '0',
  max_spins_month: '0',
  features: '',
  kiwify_checkout_url: '',
  is_active: true,
};

const TIER_LABEL: Record<Tier, string> = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
  pro_plus: 'Pro Plus',
};

const SubscriptionPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [newKiwifyId, setNewKiwifyId] = useState('');
  const [newKiwifyLabel, setNewKiwifyLabel] = useState('');

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const response = await api.get<{ success: boolean; data: SubscriptionPlan[] }>(
        `/admin/subscription-plans?${params}`
      );
      if (response.data.success) {
        setPlans(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      alert('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name,
      slug: plan.slug,
      tier: plan.tier,
      billing_cycle: plan.billing_cycle ?? '',
      price_monthly: plan.price_monthly?.toString() ?? '',
      price_yearly: plan.price_yearly?.toString() ?? '',
      max_wheels: plan.max_wheels.toString(),
      max_products: plan.max_products.toString(),
      max_spins_month: plan.max_spins_month.toString(),
      features: (plan.features ?? []).join('\n'),
      kiwify_checkout_url: plan.kiwify_checkout_url ?? '',
      is_active: plan.is_active,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPlan(null);
    setForm(EMPTY_FORM);
    setNewKiwifyId('');
    setNewKiwifyLabel('');
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    slug: form.slug.trim(),
    tier: form.tier,
    billing_cycle: form.tier === 'free' ? null : form.billing_cycle || null,
    price_monthly: form.price_monthly === '' ? null : Number(form.price_monthly),
    price_yearly: form.price_yearly === '' ? null : Number(form.price_yearly),
    max_wheels: parseInt(form.max_wheels, 10),
    max_products: parseInt(form.max_products, 10),
    max_spins_month: parseInt(form.max_spins_month, 10),
    features: form.features
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean),
    kiwify_checkout_url: form.kiwify_checkout_url.trim() || null,
    is_active: form.is_active,
  });

  const handleSave = async () => {
    if (!form.name.trim() || !form.slug.trim()) {
      alert('Nome e slug são obrigatórios');
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      if (editingPlan) {
        await api.patch(`/admin/subscription-plans/${editingPlan.id}`, payload);
      } else {
        await api.post('/admin/subscription-plans', payload);
      }
      await loadPlans();
      closeModal();
    } catch (error: any) {
      console.error('Erro ao salvar plano:', error);
      alert(error?.response?.data?.message || 'Erro ao salvar plano');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (plan: SubscriptionPlan) => {
    if (!window.confirm(`Desativar o plano "${plan.name}"? Ele deixará de aparecer nas listagens.`)) return;

    try {
      await api.delete(`/admin/subscription-plans/${plan.id}`);
      loadPlans();
    } catch (error: any) {
      console.error('Erro ao desativar plano:', error);
      alert(error?.response?.data?.message || 'Erro ao desativar plano');
    }
  };

  const handleAddKiwifyId = async () => {
    if (!editingPlan) return;
    const id = newKiwifyId.trim();
    if (!id) {
      alert('Informe o Kiwify plan_id');
      return;
    }
    try {
      await api.post(`/admin/subscription-plans/${editingPlan.id}/kiwify-ids`, {
        kiwify_plan_id: id,
        label: newKiwifyLabel.trim() || null,
      });
      setNewKiwifyId('');
      setNewKiwifyLabel('');
      await loadPlans();
      // Atualiza o plano em edição com os mapeamentos novos
      const refreshed = (
        await api.get<{ success: boolean; data: SubscriptionPlan }>(
          `/admin/subscription-plans/${editingPlan.id}`
        )
      ).data.data;
      setEditingPlan(refreshed);
    } catch (error: any) {
      console.error('Erro ao adicionar Kiwify ID:', error);
      alert(error?.response?.data?.message || 'Erro ao adicionar Kiwify ID');
    }
  };

  const handleRemoveKiwifyId = async (mapping: KiwifyMapping) => {
    if (!editingPlan) return;
    if (!window.confirm(`Remover o Kiwify ID "${mapping.kiwify_plan_id}" deste plano?`)) return;

    try {
      await api.delete(
        `/admin/subscription-plans/${editingPlan.id}/kiwify-ids/${mapping.id}`
      );
      await loadPlans();
      const refreshed = (
        await api.get<{ success: boolean; data: SubscriptionPlan }>(
          `/admin/subscription-plans/${editingPlan.id}`
        )
      ).data.data;
      setEditingPlan(refreshed);
    } catch (error: any) {
      console.error('Erro ao remover Kiwify ID:', error);
      alert(error?.response?.data?.message || 'Erro ao remover Kiwify ID');
    }
  };

  const formatLimit = (n: number) => (n === -1 ? 'Ilimitado' : n.toString());
  const formatPrice = (n: number | null) => (n === null ? '—' : `R$ ${Number(n).toFixed(2)}`);

  const tierBadge = (tier: Tier) => {
    const colors: Record<Tier, string> = {
      free: 'bg-gray-100 text-gray-800',
      basic: 'bg-blue-100 text-blue-800',
      pro: 'bg-purple-100 text-purple-800',
      pro_plus: 'bg-amber-100 text-amber-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[tier]}`}>
        {TIER_LABEL[tier]}
      </span>
    );
  };

  const cycleBadge = (cycle: BillingCycle) => {
    if (!cycle) return <span className="text-xs text-gray-400">—</span>;
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
        {cycle === 'monthly' ? 'Mensal' : 'Anual'}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planos de Assinatura</h1>
          <p className="text-gray-600">Total: {plans.length} planos</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Novo Plano
        </button>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex gap-4">
        <input
          type="text"
          placeholder="Buscar por nome ou slug..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {loading ? (
        <div className="text-center py-8">Carregando...</div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plano</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ciclo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço Mensal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço Anual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Limites</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Checkout</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kiwify IDs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{plan.name}</div>
                      <div className="text-xs text-gray-500">{plan.slug}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{tierBadge(plan.tier)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{cycleBadge(plan.billing_cycle)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatPrice(plan.price_monthly)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatPrice(plan.price_yearly)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-700">
                      <div>{formatLimit(plan.max_wheels)} sorteios</div>
                      <div>{formatLimit(plan.max_products)} produtos</div>
                      <div>{formatLimit(plan.max_spins_month)} giros/mês</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {plan.kiwify_checkout_url ? (
                        <a
                          href={plan.kiwify_checkout_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                          title={plan.kiwify_checkout_url}
                        >
                          <ExternalLink className="h-4 w-4" />
                          Abrir
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {plan.kiwify_mappings.length}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{plan.companies_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          plan.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {plan.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEdit(plan)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {plan.is_active && (
                          <button
                            onClick={() => handleDeactivate(plan)}
                            className="text-red-600 hover:text-red-800"
                            title="Desativar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de criação/edição */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingPlan ? `Editar plano: ${editingPlan.name}` : 'Novo plano'}
        size="xl"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug * (único)</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tier *</label>
              <select
                value={form.tier}
                onChange={(e) =>
                  setForm({
                    ...form,
                    tier: e.target.value as Tier,
                    billing_cycle: e.target.value === 'free' ? '' : form.billing_cycle || 'monthly',
                  })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="pro">Pro</option>
                <option value="pro_plus">Pro Plus</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ciclo de cobrança *</label>
              <select
                value={form.billing_cycle}
                onChange={(e) =>
                  setForm({ ...form, billing_cycle: e.target.value as FormState['billing_cycle'] })
                }
                disabled={form.tier === 'free'}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">{form.tier === 'free' ? 'N/A (Free)' : 'Selecione…'}</option>
                <option value="monthly">Mensal</option>
                <option value="yearly">Anual</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preço mensal (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.price_monthly}
                onChange={(e) => setForm({ ...form, price_monthly: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Preço anual (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.price_yearly}
                onChange={(e) => setForm({ ...form, price_yearly: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sorteios (-1 = ilimitado)</label>
              <input
                type="number"
                value={form.max_wheels}
                onChange={(e) => setForm({ ...form, max_wheels: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Produtos (-1 = ilimitado)</label>
              <input
                type="number"
                value={form.max_products}
                onChange={(e) => setForm({ ...form, max_products: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Giros/mês (-1 = ilimitado)</label>
              <input
                type="number"
                value={form.max_spins_month}
                onChange={(e) => setForm({ ...form, max_spins_month: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link de checkout Kiwify</label>
            <input
              type="url"
              value={form.kiwify_checkout_url}
              onChange={(e) => setForm({ ...form, kiwify_checkout_url: e.target.value })}
              placeholder="https://pay.kiwify.com.br/..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              URL pública padrão desta variante. Ofertas custom por cliente (gerenciadas pelos Kiwify IDs abaixo) têm links próprios — não preenchidos aqui.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Features (uma por linha)</label>
            <textarea
              rows={5}
              value={form.features}
              onChange={(e) => setForm({ ...form, features: e.target.value })}
              placeholder={'Ex.:\n10 Sorteios\n200 Produtos\nSuporte prioritário'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="is_active"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700">
              Ativo (aparece nas listagens)
            </label>
          </div>

          {/* Mapeamentos Kiwify — só visível em modo de edição */}
          {editingPlan && (
            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Kiwify plan IDs ({editingPlan.kiwify_mappings.length})</h4>
              <p className="text-xs text-gray-500 mb-3">
                Vários IDs Kiwify podem mapear para o mesmo plano (útil para ofertas custom de clientes).
              </p>

              {editingPlan.kiwify_mappings.length > 0 && (
                <div className="space-y-2 mb-4">
                  {editingPlan.kiwify_mappings.map((m) => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono text-gray-900 truncate">{m.kiwify_plan_id}</div>
                        {m.label && <div className="text-xs text-gray-500">{m.label}</div>}
                      </div>
                      <button
                        onClick={() => handleRemoveKiwifyId(m)}
                        className="ml-3 text-red-500 hover:text-red-700"
                        title="Remover"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Kiwify plan_id (UUID)"
                  value={newKiwifyId}
                  onChange={(e) => setNewKiwifyId(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                />
                <input
                  type="text"
                  placeholder="Label (opcional)"
                  value={newKiwifyLabel}
                  onChange={(e) => setNewKiwifyLabel(e.target.value)}
                  className="w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <button
                  onClick={handleAddKiwifyId}
                  className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  <Plus className="h-4 w-4" />
                  Adicionar
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={closeModal}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Salvando…' : editingPlan ? 'Salvar alterações' : 'Criar plano'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SubscriptionPlansPage;
